import { Link, useLocation } from "react-router-dom";
import { Search, Menu, X, Heart, Globe, Home, MapPin, Mail } from "lucide-react";
import { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { useFavorites } from "@/context/FavoritesContext";

const NAV_ITEMS = [
  { to: "/", key: "nav.home", Icon: Home, testid: "nav-link-home" },
  { to: "/map", key: "nav.map", Icon: MapPin, testid: "nav-link-map" },
  { to: "/favorites", key: "nav.favorites", Icon: Heart, testid: "nav-link-favorites" },
  { to: "/contact", key: "nav.contact", Icon: Mail, testid: "nav-link-contact" },
];

export const Navbar = ({ onSearch, searchValue = "", large = false }) => {
  const { t, lang, toggleLang } = useLang();
  const { pathname } = useLocation();
  const { favorites } = useFavorites();
  const [open, setOpen] = useState(false);

  const logoSize = large ? "h-12 w-12" : "h-9 w-9";
  const nameSize = large ? "text-2xl sm:text-3xl" : "text-xl";
  const barHeight = large ? "h-20" : "h-16";

  const LangToggle = ({ className = "" }) => (
    <button
      onClick={toggleLang}
      data-testid="nav-lang-toggle"
      className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-bold text-muted-foreground hover:text-foreground transition-colors ${className}`}
    >
      <Globe size={16} /> {lang === "el" ? "EN" : "EL"}
    </button>
  );

  return (
    <header className="sticky top-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200/80 dark:border-slate-800/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center justify-between ${barHeight} gap-4`}>
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo">
            <img src="/brand/logo.png" alt="SkillCity" className={`${logoSize} rounded-xl object-cover`} />
            <span className={`font-display ${nameSize} font-extrabold tracking-tight`}>Skill<span className="text-primary">City</span></span>
          </Link>

          {onSearch && (
            <div className="hidden md:flex flex-1 max-w-md relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="nav-search-input"
                value={searchValue}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={t("nav.searchPlaceholder")}
                className="w-full pl-10 pr-4 h-10 rounded-full bg-muted border border-transparent focus:border-primary focus:bg-background outline-none text-sm transition-colors"
              />
            </div>
          )}

          <nav className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map(({ to, key, Icon, testid }) => {
              const active = to === "/" ? pathname === "/" : pathname.startsWith(to);
              return (
                <Link key={to} to={to} data-testid={testid}
                  className={`relative flex items-center gap-2 px-4 h-11 rounded-full text-[15px] transition-colors ${active ? "bg-[#EAF3FF] text-[#0B5ED7] font-bold" : "text-[#2B3550] font-semibold hover:text-[#0B5ED7]"}`}>
                  <Icon size={20} strokeWidth={2} fill={active && to === "/" ? "currentColor" : "none"} />
                  {t(key)}
                  {to === "/favorites" && favorites.length > 0 && (
                    <span className="h-5 min-w-[1.25rem] px-1 grid place-items-center rounded-full bg-rose-500 text-white text-[11px] font-bold">{favorites.length}</span>
                  )}
                  {active && <span className="absolute left-5 right-5 -bottom-[9px] h-[3px] rounded-full bg-[#0B5ED7]" />}
                </Link>
              );
            })}
            <LangToggle />
          </nav>

          <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden pb-4 space-y-2" data-testid="nav-mobile-menu">
            {onSearch && (
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={searchValue}
                  onChange={(e) => onSearch(e.target.value)}
                  placeholder={t("nav.searchPlaceholder")}
                  className="w-full pl-10 pr-4 h-10 rounded-full bg-muted outline-none text-sm"
                />
              </div>
            )}
            <Link to="/" onClick={() => setOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-muted font-semibold">{t("nav.home")}</Link>
            <Link to="/map" onClick={() => setOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-muted font-semibold">{t("nav.map")}</Link>
            <Link to="/favorites" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted font-semibold">
              <Heart size={16} /> {t("nav.favorites")} {favorites.length > 0 && <span className="h-5 min-w-[1.25rem] px-1 grid place-items-center rounded-full bg-rose-500 text-white text-[11px] font-bold">{favorites.length}</span>}
            </Link>
            <Link to="/contact" onClick={() => setOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-muted font-semibold">{t("nav.contact")}</Link>
            <LangToggle className="w-full justify-start" />
          </div>
        )}
      </div>
    </header>
  );
};
